import { z } from "zod";
import { bedtimeBookDesignV1Schema } from "./bedtime-book-design.js";
import { bookPageCompositionPlanV1Schema, bookPageCompositionRequestV1Schema, bookPageRectV1Schema, } from "./book-page-composition.js";
export const BEDTIME_PUBLICATION_PLAN_CONTRACT = "BEDTIME_PUBLICATION_PLAN_V1";
export const BEDTIME_PUBLICATION_TRUTH_BOUNDARY = "COMPILED_PUBLICATION_PLAN_NOT_RENDERED_OR_PRINT_APPROVED";
const idSchema = z.string().trim().min(1).max(240);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Z][A-Z0-9_]*$/u);
const digestSchema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
export const bedtimeAuthorityBindingV1Schema = z
    .object({
    id: idSchema,
    revision: z.number().int().nonnegative(),
    digestSha256: digestSchema,
})
    .strict();
export const bedtimeTextMomentRoleSchema = z.enum([
    "BODY",
    "DIALOGUE",
    "DISPLAY",
    "REFRAIN",
    "COUNT",
    "REACTION",
    "WHISPER",
]);
export const bedtimeOpticalInsetPresetSchema = z.enum(["TIGHT", "BALANCED", "GENEROUS"]);
export const bedtimeTextMomentV1Schema = z
    .object({
    contract: z.literal("BEDTIME_TEXT_MOMENT_V1"),
    textMomentId: idSchema,
    sequence: z.number().int().min(1).max(3),
    role: bedtimeTextMomentRoleSchema,
    approvedText: z.string().trim().min(1).max(1_000),
    approvedLines: z.array(z.string().trim().min(1).max(500)).min(1).max(8),
    lineationAuthority: z.enum(["APPROVED_STORYBOARD_LINES", "EXACT_COMPILER_REFLOW"]),
    noBreakPhrases: z.array(z.string().trim().min(1).max(240)).max(12),
    preferredAlignment: z.enum(["START", "CENTER", "END"]),
    preferredVerticalAlignment: z.enum(["TOP", "CENTER", "BOTTOM"]),
    preferredSurface: z.enum(["ART", "PAPER"]),
    preferredFontSizeMilliPoints: z.number().int().min(12_000).max(72_000),
    preferredLineHeightMillionths: z.number().int().min(1_000_000).max(1_400_000),
    preferredWeight: z.enum(["REGULAR", "SEMIBOLD"]),
    preferredFillColorRgba: z.string().regex(/^#[A-F0-9]{8}$/u),
    preferredOpticalInsetPreset: bedtimeOpticalInsetPresetSchema.optional(),
    /**
     * Designed books (Phase 1c, owner decision A 2026-10-10): up to two whole words of the
     * approved text that the storyboard marked for the kit's italic. Read-only for the buyer.
     */
    emphasisWords: z.array(z.string().trim().min(1).max(40)).min(1).max(2).optional(),
    /**
     * Designed books (corpus comparison 2026-10-10): one whole word of the approved text that
     * performs, a sound or surprise word, set in the kit's display lettering and accent ink.
     */
    displayWord: z.string().trim().min(1).max(40).optional(),
    momentDigestSha256: digestSchema,
})
    .strict()
    .superRefine((moment, context) => {
    const normalized = (value) => value.normalize("NFC").replace(/\s+/gu, " ").trim();
    if (normalized(moment.approvedLines.join(" ")) !== normalized(moment.approvedText))
        context.addIssue({
            code: "custom",
            path: ["approvedLines"],
            message: "Approved moment lines must contain the complete approved moment text.",
        });
    for (const [index, phrase] of moment.noBreakPhrases.entries())
        if (!normalized(moment.approvedText)
            .toLocaleLowerCase("en-US")
            .includes(normalized(phrase).toLocaleLowerCase("en-US")))
            context.addIssue({
                code: "custom",
                path: ["noBreakPhrases", index],
                message: "A no-break phrase must occur in the approved text moment.",
            });
});
export const bedtimeNarrativeBeatV1Schema = z
    .object({
    contract: z.literal("BEDTIME_NARRATIVE_BEAT_V1"),
    beatId: idSchema,
    // Eight beats on the first profile, twelve in a picture book (owner decision 1, 2026-11-02).
    sequence: z.number().int().min(1).max(12),
    manuscriptNodeIds: z.array(idSchema).min(1).max(20),
    sourceDecisionIds: z.array(idSchema).min(1).max(100),
    protectedFactIds: z.array(idSchema).max(100),
    acceptedMeaningDigestSha256: digestSchema,
    approvedText: z.string().trim().min(1).max(2_000).optional(),
    textMoments: z.array(bedtimeTextMomentV1Schema).min(1).max(3).optional(),
    emotionalBeat: z.string().trim().min(1).max(500).optional(),
    pageTurnReveal: z.string().trim().min(1).max(500).optional(),
    /** What the beat's picture shows, from the approved storyboard; its pictures' description. */
    illustrationDescription: z.string().trim().min(1).max(1_000).optional(),
})
    .strict()
    .superRefine((beat, context) => {
    if (!beat.textMoments)
        return;
    if (beat.textMoments.some(({ sequence }, index) => sequence !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["textMoments"],
            message: "Text moments must have stable contiguous reading-order sequences.",
        });
    if (new Set(beat.textMoments.map(({ textMomentId }) => textMomentId)).size !==
        beat.textMoments.length)
        context.addIssue({
            code: "custom",
            path: ["textMoments"],
            message: "Text-moment IDs must be unique within a narrative beat.",
        });
    const normalize = (value) => value.normalize("NFC").replace(/\s+/gu, " ").trim();
    if (beat.approvedText &&
        normalize(beat.textMoments.map(({ approvedText }) => approvedText).join(" ")) !==
            normalize(beat.approvedText))
        context.addIssue({
            code: "custom",
            path: ["textMoments"],
            message: "Text moments must preserve the complete approved beat in reading order.",
        });
});
export const bedtimeReadingTurnKindSchema = z.enum(["TEXT_BEARING", "WORDLESS_BREATH"]);
export const bedtimeReadingTurnV1Schema = z
    .object({
    contract: z.literal("BEDTIME_READING_TURN_V1"),
    turnId: idSchema,
    sequence: z.number().int().min(1).max(12),
    kind: bedtimeReadingTurnKindSchema,
    sourceBeatIds: z.array(idSchema).min(1).max(8),
    manuscriptNodeIds: z.array(idSchema).max(40),
    sourceDecisionIds: z.array(idSchema).min(1).max(100),
    extensionPolicy: z.enum(["NOT_APPLICABLE", "EXTEND_ACCEPTED_STATE_ONLY"]),
    prohibitedInventions: z.array(idSchema).max(100),
    pageTurnPurpose: z.string().trim().min(1).max(500),
    accessibleDescription: z.string().trim().min(1).max(1_000).optional(),
    grammarId: idSchema,
    layoutVariantId: idSchema.optional(),
    physicalPageIds: z.tuple([idSchema, idSchema]),
    /**
     * A designed book may carry one beat's words across two turns (owner decision 2026-10-10:
     * fewer wordless spreads). Each turn then names the beat's text moments it carries, in
     * reading order. Absent, a turn carries every moment of its source beats.
     */
    textMomentIds: z.array(idSchema).min(1).max(3).optional(),
})
    .strict()
    .superRefine((turn, context) => {
    if (turn.textMomentIds && (turn.kind !== "TEXT_BEARING" || turn.sourceBeatIds.length !== 1))
        context.addIssue({
            code: "custom",
            path: ["textMomentIds"],
            message: "Only a text-bearing turn of one beat may carry part of that beat's words.",
        });
    if (turn.kind === "TEXT_BEARING" &&
        (turn.manuscriptNodeIds.length === 0 || turn.extensionPolicy !== "NOT_APPLICABLE"))
        context.addIssue({
            code: "custom",
            path: ["kind"],
            message: "A text-bearing turn requires manuscript nodes and cannot be an extension.",
        });
    if (turn.kind === "WORDLESS_BREATH" &&
        (turn.manuscriptNodeIds.length > 0 ||
            turn.extensionPolicy !== "EXTEND_ACCEPTED_STATE_ONLY" ||
            turn.prohibitedInventions.length === 0))
        context.addIssue({
            code: "custom",
            path: ["kind"],
            message: "A wordless breath has no manuscript text and must explicitly prohibit invented content.",
        });
});
/** The text moments a turn carries: the moments it names, or every moment of its source beats. */
export function bedtimeTurnTextMoments(beats, turn) {
    if (turn.kind !== "TEXT_BEARING")
        return [];
    const moments = turn.sourceBeatIds.flatMap((beatId) => beats.find((beat) => beat.beatId === beatId)?.textMoments ?? []);
    return turn.textMomentIds
        ? moments.filter(({ textMomentId }) => turn.textMomentIds.includes(textMomentId))
        : moments;
}
export const bedtimeMediaTopologySchema = z.enum([
    "SINGLE_PAGE",
    "FACING_SPREAD",
    "SPOT",
    "SEQUENTIAL_REGIONS",
]);
const bedtimeSourcePanelV1Schema = z
    .object({
    slotId: idSchema,
    destinationRegion: bookPageRectV1Schema,
    sourceCropPixels: z
        .object({
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
export const bedtimeMediaSurfaceV1Schema = z
    .object({
    contract: z.literal("BEDTIME_MEDIA_SURFACE_V1"),
    mediaSurfaceId: idSchema,
    turnId: idSchema,
    topology: bedtimeMediaTopologySchema,
    widthPixels: z.number().int().positive().max(100_000),
    heightPixels: z.number().int().positive().max(100_000),
    targetDpi: z.number().int().min(150).max(600),
    cropPolicy: z.enum(["NONE", "APPROVED_FOCAL_CROP_ONLY"]),
    quietRegions: z.array(bookPageRectV1Schema).max(20),
    protectedRegions: z.array(bookPageRectV1Schema).max(50),
    physicalPageIds: z.array(idSchema).min(1).max(2),
    sourcePanelPacking: z.literal("VERTICAL_NATIVE_PANELS").optional(),
    // Two pictures of a beat, or three (2026-10-10: a three-step sequence).
    sourcePanels: z.array(bedtimeSourcePanelV1Schema).min(2).max(3).optional(),
})
    .strict()
    .superRefine((surface, context) => {
    const expectedPages = surface.topology === "FACING_SPREAD" || surface.topology === "SEQUENTIAL_REGIONS" ? 2 : 1;
    if (surface.physicalPageIds.length !== expectedPages)
        context.addIssue({
            code: "custom",
            path: ["physicalPageIds"],
            message: "Media topology must bind its exact physical-page extent.",
        });
    if (Boolean(surface.sourcePanelPacking) !== Boolean(surface.sourcePanels))
        context.addIssue({
            code: "custom",
            path: ["sourcePanels"],
            message: "Packed media requires both its explicit packing policy and exact source panels.",
        });
    if (!surface.sourcePanels)
        return;
    if (surface.topology !== "SEQUENTIAL_REGIONS" ||
        new Set(surface.sourcePanels.map(({ slotId }) => slotId)).size !== surface.sourcePanels.length)
        context.addIssue({
            code: "custom",
            path: ["sourcePanels"],
            message: "Only sequential media may pack two uniquely bound native source panels.",
        });
    for (const [index, panel] of surface.sourcePanels.entries()) {
        const crop = panel.sourceCropPixels;
        const region = panel.destinationRegion;
        if (crop.x + crop.width > surface.widthPixels ||
            crop.y + crop.height > surface.heightPixels ||
            crop.width < Math.ceil((region.widthMilliPoints / 72_000) * surface.targetDpi) ||
            crop.height < Math.ceil((region.heightMilliPoints / 72_000) * surface.targetDpi) ||
            Math.abs(crop.width / crop.height / (region.widthMilliPoints / region.heightMilliPoints) - 1) > 0.015)
            context.addIssue({
                code: "custom",
                path: ["sourcePanels", index],
                message: "A packed crop must fit its canvas and provide native pixels on both axes without distortion.",
            });
        const previous = surface.sourcePanels[index - 1]?.sourceCropPixels;
        if (previous && crop.y < previous.y + previous.height + 16)
            context.addIssue({
                code: "custom",
                path: ["sourcePanels", index, "sourceCropPixels"],
                message: "Vertical source panels must preserve reading order with a sixteen-pixel separator.",
            });
    }
});
export const bedtimePhysicalPageV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PHYSICAL_PAGE_V1"),
    pageId: idSchema,
    productionOrdinal: z.number().int().min(1).max(32),
    side: z.enum(["VERSO", "RECTO"]),
    governedRole: safeCodeSchema,
    readingTurnId: idSchema.nullable(),
})
    .strict();
export const bedtimePageGrammarIdSchema = z.enum([
    "ART_VERSO_TEXT_RECTO",
    "TEXT_VERSO_ART_RECTO",
    "FULL_SPREAD_QUIET_ZONE",
    "CROSS_SPINE_MIXED_FIELD",
    "ART_WITH_PAGE_TEXT",
    "VIGNETTE_ON_PAPER",
    "SEQUENTIAL_MOMENTS",
    "WORDLESS_SPREAD",
    "EXTERNAL_RAIL_DIGITAL",
]);
const bedtimePageGrammarSlotV1Schema = z
    .object({
    slotId: idSchema,
    kind: z.enum(["TEXT", "MEDIA"]),
    pageSide: z.enum(["VERSO", "RECTO", "CROSS_SPINE"]),
    region: bookPageRectV1Schema,
})
    .strict();
export const bedtimePageGrammarVariantV1Schema = z
    .object({
    variantId: idSchema,
    editorialPurpose: z.string().trim().min(1).max(500),
    slots: z.array(bedtimePageGrammarSlotV1Schema).min(1).max(12),
    visualOrder: z.array(idSchema).min(1).max(20),
    drawOrder: z.array(idSchema).min(1).max(20),
    accessibilityOrder: z.array(idSchema).max(20),
    sourcePanelPacking: z.literal("VERTICAL_NATIVE_PANELS").optional(),
})
    .strict();
export const bedtimePageGrammarV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PAGE_GRAMMAR_V1"),
    grammarId: bedtimePageGrammarIdSchema,
    physicalEligible: z.boolean(),
    eligibleTurnKinds: z.array(bedtimeReadingTurnKindSchema).min(1).max(2),
    slots: z.array(bedtimePageGrammarSlotV1Schema).min(1).max(12),
    layoutVariants: z.array(bedtimePageGrammarVariantV1Schema).min(1).max(24).optional(),
    textBehavior: z.enum(["LIVE_TYPE", "WORDLESS", "DIGITAL_REFLOW_RAIL"]),
    allowedMediaTopologies: z.array(bedtimeMediaTopologySchema).min(1).max(4),
    visualOrder: z.array(idSchema).min(1).max(20),
    drawOrder: z.array(idSchema).min(1).max(20),
    accessibilityOrder: z.array(idSchema).max(20),
    fallbackGrammarIds: z.array(bedtimePageGrammarIdSchema).max(7),
    validationPolicy: z.literal("BLOCK_NO_SILENT_REFLOW_SHRINK_CLIP_OR_TOPOLOGY_CHANGE"),
})
    .strict()
    .superRefine((grammar, context) => {
    if (grammar.grammarId === "EXTERNAL_RAIL_DIGITAL" && grammar.physicalEligible)
        context.addIssue({
            code: "custom",
            path: ["physicalEligible"],
            message: "External rail is a digital fallback and cannot be physical-edition eligible.",
        });
    if (grammar.textBehavior === "WORDLESS" &&
        grammar.eligibleTurnKinds.some((kind) => kind !== "WORDLESS_BREATH"))
        context.addIssue({
            code: "custom",
            path: ["eligibleTurnKinds"],
            message: "Wordless grammar can accept only a wordless reading turn.",
        });
    const variants = grammar.layoutVariants ?? [];
    if (new Set(variants.map(({ variantId }) => variantId)).size !== variants.length)
        context.addIssue({
            code: "custom",
            path: ["layoutVariants"],
            message: "A grammar cannot contain duplicate governed layout-variant IDs.",
        });
    for (const [index, variant] of variants.entries()) {
        if (variant.sourcePanelPacking &&
            (grammar.grammarId !== "SEQUENTIAL_MOMENTS" ||
                !grammar.allowedMediaTopologies.includes("SEQUENTIAL_REGIONS") ||
                ![2, 3].includes(variant.slots.filter(({ kind }) => kind === "MEDIA").length) ||
                !["VERSO", "RECTO"].every((side) => variant.slots.some(({ kind, pageSide }) => kind === "MEDIA" && pageSide === side))))
            context.addIssue({
                code: "custom",
                path: ["layoutVariants", index, "sourcePanelPacking"],
                message: "Native source packing requires two or three governed panels, with at least one on each side of a sequential turn.",
            });
        const slotIds = new Set(variant.slots.map(({ slotId }) => slotId));
        for (const [orderName, orderedIds] of [
            ["visualOrder", variant.visualOrder],
            ["drawOrder", variant.drawOrder],
            ["accessibilityOrder", variant.accessibilityOrder],
        ])
            if (orderedIds.some((slotId) => !slotIds.has(slotId)))
                context.addIssue({
                    code: "custom",
                    path: ["layoutVariants", index, orderName],
                    message: "Every governed layout-variant order entry must reference one of its slots.",
                });
    }
});
export const bedtimePublicationAuthorityV1Schema = z
    .object({
    storyPromise: bedtimeAuthorityBindingV1Schema,
    visualPromise: bedtimeAuthorityBindingV1Schema,
    manuscript: bedtimeAuthorityBindingV1Schema,
    storyboard: bedtimeAuthorityBindingV1Schema,
    pacing: bedtimeAuthorityBindingV1Schema,
    guardianEvidence: bedtimeAuthorityBindingV1Schema,
    factEvidence: bedtimeAuthorityBindingV1Schema,
    likenessEvidence: bedtimeAuthorityBindingV1Schema,
    consentEvidence: bedtimeAuthorityBindingV1Schema,
    approvalEvidence: bedtimeAuthorityBindingV1Schema,
    editionPolicy: bedtimeAuthorityBindingV1Schema,
    printProfile: bedtimeAuthorityBindingV1Schema,
    sourceRevision: bedtimeAuthorityBindingV1Schema,
    typographyDirection: bedtimeAuthorityBindingV1Schema.optional(),
    /** Present only when the buyer chose a book design; books without one keep the classic layout. */
    bookDesign: bedtimeAuthorityBindingV1Schema.optional(),
})
    .strict();
export const bedtimeTypographyDirectionDecisionV1Schema = z
    .object({
    contract: z.literal("BEDTIME_TYPOGRAPHY_DIRECTION_DECISION_V1"),
    decisionId: idSchema,
    beatId: idSchema,
    revision: z.number().int().positive(),
    sourceStoryboardId: idSchema,
    sourceStoryboardRevision: z.number().int().nonnegative(),
    sourceStoryboardDigestSha256: digestSchema,
    sourceApprovedTextDigestSha256: digestSchema,
    sourceAcceptedMeaningDigestSha256: digestSchema,
    moments: z.array(bedtimeTextMomentV1Schema).min(1).max(3),
    /**
     * The moments whose type size the owner chose. A designed book sets every other story
     * moment at its kit's size and leading, whatever size the decision carries.
     */
    sizeChosenMomentIds: z.array(idSchema).min(1).max(3).optional(),
    decidedAt: timestampSchema,
    decidedByActorId: idSchema,
    decisionDigestSha256: digestSchema,
    truthBoundary: z.literal("PUBLICATION_TYPOGRAPHY_DIRECTION_PRESERVES_APPROVED_WORDING"),
})
    .strict()
    .superRefine((decision, context) => {
    const momentIds = new Set(decision.moments.map(({ textMomentId }) => textMomentId));
    if (decision.sizeChosenMomentIds?.some((id) => !momentIds.has(id)))
        context.addIssue({
            code: "custom",
            path: ["sizeChosenMomentIds"],
            message: "A chosen type size must belong to one of the decision's moments.",
        });
    if (decision.moments.some(({ sequence }, index) => sequence !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["moments"],
            message: "Typography-direction moments require contiguous reading-order sequences.",
        });
    if (new Set(decision.moments.map(({ textMomentId }) => textMomentId)).size !==
        decision.moments.length)
        context.addIssue({
            code: "custom",
            path: ["moments"],
            message: "Typography-direction moments require stable unique identities.",
        });
});
export const bedtimeTypesettingEvidenceV1Schema = z
    .object({
    contract: z.literal("BEDTIME_TYPESETTING_EVIDENCE_V1"),
    engineVersion: z.literal("FONTKIT_PDF_LIB_WIDTHS_V1"),
    lineBreakPolicy: z.enum([
        "PHRASE_AWARE_NO_HYPHENATION_V1",
        "SEMANTIC_MOMENT_LOCKS_NO_HYPHENATION_V2",
        // Designed books also keep a line from ending on a word that opens the next phrase.
        "PICTURE_BOOK_PHRASES_NO_HYPHENATION_V3",
    ]),
    fontDigestSha256: digestSchema,
    semanticBindingDigestSha256: digestSchema.optional(),
    lineMeasurements: z
        .array(z
        .object({
        advanceWidthMilliPoints: z.number().int().nonnegative().max(40_000_000),
        glyphCount: z.number().int().nonnegative().max(10_000),
        inkBoundsMilliPoints: z
            .object({
            minX: z.number().int().min(-5_000_000).max(5_000_000),
            minY: z.number().int().min(-5_000_000).max(5_000_000),
            maxX: z.number().int().min(-5_000_000).max(5_000_000),
            maxY: z.number().int().min(-5_000_000).max(5_000_000),
        })
            .strict(),
    })
        .strict())
        .min(1)
        .max(100),
    maximumLineWidthMilliPoints: z.number().int().positive().max(40_000_000),
    measuredHeightMilliPoints: z.number().int().positive().max(40_000_000),
    measurementDigestSha256: digestSchema,
})
    .strict();
export const bedtimeTypeDirectionV1Schema = z
    .object({
    contract: z.literal("BEDTIME_TYPE_DIRECTION_V1"),
    textMomentId: idSchema,
    momentSequence: z.number().int().min(1).max(3),
    semanticRole: bedtimeTextMomentRoleSchema,
    treatment: z.enum([
        "BODY",
        "DISPLAY",
        "REFRAIN",
        "COUNT",
        "REACTION",
        "WHISPER",
        "STEPPED_DIALOGUE",
    ]),
    lineationAuthority: z.enum(["APPROVED_STORYBOARD_LINES", "EXACT_COMPILER_REFLOW"]),
    noBreakPhrases: z.array(z.string().trim().min(1).max(240)).max(12),
    resolvedSurface: z.enum(["ART", "PAPER"]),
    grammarSlotId: idSchema,
    outerSlotRegion: bookPageRectV1Schema,
    opticalInsetPolicy: z.literal("GOVERNED_OPTICAL_INSET_V1"),
    opticalInsetsMilliPoints: z
        .object({
        top: z.number().int().nonnegative().max(2_000_000),
        right: z.number().int().nonnegative().max(2_000_000),
        bottom: z.number().int().nonnegative().max(2_000_000),
        left: z.number().int().nonnegative().max(2_000_000),
    })
        .strict(),
    directionDigestSha256: digestSchema,
})
    .strict();
export const bedtimeTypesetTextBlockV1Schema = z
    .object({
    textBlockId: idSchema,
    turnId: idSchema,
    manuscriptNodeIds: z.array(idSchema).min(1).max(40),
    font: z.object({ fontId: idSchema, fileName: idSchema, digestSha256: digestSchema }).strict(),
    role: safeCodeSchema,
    measuredLines: z.array(z.string().max(500)).min(1).max(100),
    /**
     * A designed book's emphasised words: each measured line as ordered runs in the body face or
     * the emphasis face. Every line's runs join to exactly that measured line.
     */
    emphasis: z
        .object({
        font: z
            .object({ fontId: idSchema, fileName: idSchema, digestSha256: digestSchema })
            .strict(),
        /** The performing word's display face and, on paper, its accent ink. */
        display: z
            .object({
            font: z
                .object({ fontId: idSchema, fileName: idSchema, digestSha256: digestSchema })
                .strict(),
            fillColorRgba: z.string().regex(/^#[A-F0-9]{8}$/u),
        })
            .strict()
            .optional(),
        lineRuns: z
            .array(z
            .array(z
            .object({
            text: z.string().min(1).max(500),
            emphasis: z.boolean(),
            display: z.literal(true).optional(),
        })
            .strict())
            .min(1)
            .max(20))
            .min(1)
            .max(100),
    })
        .strict()
        .optional(),
    region: bookPageRectV1Schema,
    visualOrderIndex: z.number().int().nonnegative(),
    drawOrderIndex: z.number().int().nonnegative(),
    accessibilityOrderIndex: z.number().int().nonnegative(),
    typography: z
        .object({
        fontSizeMilliPoints: z.number().int().min(12_000).max(72_000),
        minimumFontSizeMilliPoints: z.number().int().min(12_000).max(72_000),
        lineHeightMilliPoints: z.number().int().min(12_000).max(100_000),
        letterSpacingMilliPoints: z.number().int().min(-5_000).max(20_000),
        fillColorRgba: z.string().regex(/^#[A-F0-9]{8}$/u),
        horizontalAlignment: z.enum(["START", "CENTER", "END"]),
        verticalAlignment: z.enum(["TOP", "CENTER", "BOTTOM"]),
    })
        .strict()
        .superRefine((typography, context) => {
        if (typography.minimumFontSizeMilliPoints > typography.fontSizeMilliPoints) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["minimumFontSizeMilliPoints"],
                message: "Minimum font size cannot exceed the selected font size.",
            });
        }
        if (typography.lineHeightMilliPoints < typography.fontSizeMilliPoints) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lineHeightMilliPoints"],
                message: "Line height cannot be smaller than the selected font size.",
            });
        }
    })
        .optional(),
    typeDirection: bedtimeTypeDirectionV1Schema.optional(),
    typesettingEvidence: bedtimeTypesettingEvidenceV1Schema.optional(),
    fitResolution: z.enum([
        "APPROVED_REFLOW",
        "APPROVED_LINE_BREAK",
        "ALTERNATE_REGION_SAME_GRAMMAR",
    ]),
})
    .strict()
    .superRefine((block, context) => {
    if (block.typeDirection) {
        const { outerSlotRegion, opticalInsetsMilliPoints: insets } = block.typeDirection;
        const expected = {
            xMilliPoints: outerSlotRegion.xMilliPoints + insets.left,
            yMilliPoints: outerSlotRegion.yMilliPoints + insets.top,
            widthMilliPoints: outerSlotRegion.widthMilliPoints - insets.left - insets.right,
            heightMilliPoints: outerSlotRegion.heightMilliPoints - insets.top - insets.bottom,
        };
        if (expected.widthMilliPoints <= 0 ||
            expected.heightMilliPoints <= 0 ||
            expected.xMilliPoints !== block.region.xMilliPoints ||
            expected.yMilliPoints !== block.region.yMilliPoints ||
            expected.widthMilliPoints !== block.region.widthMilliPoints ||
            expected.heightMilliPoints !== block.region.heightMilliPoints)
            context.addIssue({
                code: "custom",
                path: ["region"],
                message: "The executable text region must equal its governed slot after optical insets.",
            });
    }
    const evidence = block.typesettingEvidence;
    if (!evidence)
        return;
    if (block.typeDirection &&
        evidence.semanticBindingDigestSha256 !== block.typeDirection.directionDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "semanticBindingDigestSha256"],
            message: "Typesetting evidence must bind the exact semantic type direction.",
        });
    if (evidence.fontDigestSha256 !== block.font.digestSha256)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "fontDigestSha256"],
            message: "Typesetting evidence must bind the exact governed font bytes.",
        });
    if (evidence.lineMeasurements.length !== block.measuredLines.length)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "lineMeasurements"],
            message: "Every measured line requires one exact governed-font observation.",
        });
    const maximumWidth = Math.max(0, ...evidence.lineMeasurements.map(({ advanceWidthMilliPoints }) => advanceWidthMilliPoints));
    if (maximumWidth !== evidence.maximumLineWidthMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "maximumLineWidthMilliPoints"],
            message: "Maximum line width must equal the exact widest measured line.",
        });
    if (maximumWidth > block.region.widthMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "maximumLineWidthMilliPoints"],
            message: "Governed text cannot exceed its exact physical region width.",
        });
    if (evidence.measuredHeightMilliPoints > block.region.heightMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "measuredHeightMilliPoints"],
            message: "Governed text cannot exceed its exact physical region height.",
        });
    if (block.typography &&
        evidence.measuredHeightMilliPoints !==
            block.measuredLines.length * block.typography.lineHeightMilliPoints)
        context.addIssue({
            code: "custom",
            path: ["typesettingEvidence", "measuredHeightMilliPoints"],
            message: "Measured height must equal the governed line stack height.",
        });
    for (const [index, line] of evidence.lineMeasurements.entries())
        if (line.inkBoundsMilliPoints.maxX < line.inkBoundsMilliPoints.minX ||
            line.inkBoundsMilliPoints.maxY < line.inkBoundsMilliPoints.minY)
            context.addIssue({
                code: "custom",
                path: ["typesettingEvidence", "lineMeasurements", index, "inkBoundsMilliPoints"],
                message: "Governed glyph ink bounds must be ordered.",
            });
});
export const bedtimeMatterPageV1Schema = z
    .object({
    contract: z.literal("BEDTIME_MATTER_PAGE_V1"),
    pageId: idSchema,
    productionOrdinal: z.number().int().min(1).max(32),
    governedRole: safeCodeSchema,
    layoutId: idSchema,
    textBlockIds: z.array(idSchema).min(1).max(8),
    sourceBindingIds: z.array(idSchema).min(1).max(20),
})
    .strict();
export const bedtimeCoverBriefV1Schema = z
    .object({
    contract: z.literal("BEDTIME_COVER_BRIEF_V1"),
    coverBriefId: idSchema,
    title: z.string().trim().min(1).max(160),
    byline: z.string().trim().min(1).max(200),
    backCopy: z.string().trim().min(1).max(1_200),
    sourceBindingIds: z.array(idSchema).min(1).max(20),
    geometryState: z.literal("BLOCKED_PENDING_EXACT_PROFILE_COVER_REGIONS"),
    blockerCodes: z.tuple([z.literal("COVER_REGION_GEOMETRY_REQUIRED")]),
    truthBoundary: z.literal("APPROVED_COVER_COPY_NOT_A_COMPOSED_OR_PRINT_APPROVED_COVER"),
})
    .strict();
const bedtimePublicationPlanBodyV1Schema = z
    .object({
    contract: z.literal(BEDTIME_PUBLICATION_PLAN_CONTRACT),
    publicationPlanId: idSchema,
    planRevision: z.number().int().positive(),
    bookId: idSchema,
    storyRevision: z.number().int().nonnegative(),
    createdAt: timestampSchema,
    createdByActorId: idSchema,
    authority: bedtimePublicationAuthorityV1Schema,
    physicalGeometry: z
        .object({
        profileId: idSchema,
        profileRevision: z.number().int().nonnegative(),
        pageWidthMilliPoints: z.number().int().positive().max(40_000_000),
        pageHeightMilliPoints: z.number().int().positive().max(40_000_000),
        spreadWidthMilliPoints: z.number().int().positive().max(80_000_000),
        trimInsetMilliPoints: z.number().int().nonnegative().max(5_000_000),
        safeInsetMilliPoints: z.number().int().nonnegative().max(5_000_000),
        bindingExclusionMilliPoints: z.number().int().nonnegative().max(5_000_000),
    })
        .strict()
        .optional(),
    pacingDecision: z
        .object({
        state: z.enum(["RECOMMENDED", "ACCEPTED", "CUSTOMIZED"]),
        decidedAt: timestampSchema.optional(),
        decidedByActorId: idSchema.optional(),
    })
        .strict()
        .superRefine((decision, context) => {
        const decided = decision.state !== "RECOMMENDED";
        if (decided !== Boolean(decision.decidedAt && decision.decidedByActorId))
            context.addIssue({
                code: "custom",
                path: ["state"],
                message: "Accepted or customized pacing requires exact actor and time evidence.",
            });
    })
        .optional(),
    /** The buyer's book design this plan was laid out with, when one was chosen. */
    bookDesign: bedtimeBookDesignV1Schema.optional(),
    /**
     * Eight beats, each a text turn followed in pairs by a wordless breath (the first profile), or
     * twelve beats, one text turn each with no breath (the picture book, 2026-11-02).
     */
    narrativeBeats: z
        .array(bedtimeNarrativeBeatV1Schema)
        .min(8)
        .max(12)
        .refine((beats) => beats.length === 8 || beats.length === 12, {
        message: "A Bedtime publication has eight narrative beats, or twelve in a picture book.",
    }),
    typographyDirectionDecisions: z
        .array(bedtimeTypographyDirectionDecisionV1Schema)
        .max(12)
        .optional(),
    readingTurns: z.array(bedtimeReadingTurnV1Schema).length(12),
    physicalPages: z.array(bedtimePhysicalPageV1Schema).length(32),
    mediaSurfaces: z.array(bedtimeMediaSurfaceV1Schema).length(12),
    grammarCatalog: z.array(bedtimePageGrammarV1Schema).length(9),
    typesetTextBlocks: z.array(bedtimeTypesetTextBlockV1Schema).max(100),
    matterPages: z.array(bedtimeMatterPageV1Schema).length(8).optional(),
    coverBrief: bedtimeCoverBriefV1Schema.optional(),
    compositionRequest: bookPageCompositionRequestV1Schema,
    compositionPlan: bookPageCompositionPlanV1Schema.nullable(),
    compilationState: z.enum(["COMPILED", "COMPOSITION_BLOCKED"]),
    blockingDiagnostics: z
        .array(z
        .object({
        code: safeCodeSchema,
        path: z.string().max(500),
        message: z.string().max(2_000),
    })
        .strict())
        .max(500),
    approvalState: z.enum(["DRAFT", "PACING_APPROVED", "PLAN_APPROVED", "INVALIDATED"]),
    invalidatedBy: z.array(safeCodeSchema).max(100),
    truthBoundary: z.literal(BEDTIME_PUBLICATION_TRUTH_BOUNDARY),
})
    .strict()
    .superRefine((plan, context) => {
    const ordinals = plan.physicalPages.map(({ productionOrdinal }) => productionOrdinal);
    if (ordinals.some((ordinal, index) => ordinal !== index + 1))
        context.addIssue({
            code: "custom",
            path: ["physicalPages"],
            message: "A Bedtime publication has exactly 32 ordered physical pages.",
        });
    if (plan.physicalPages.some((page) => page.side !== (page.productionOrdinal % 2 === 0 ? "VERSO" : "RECTO")))
        context.addIssue({
            code: "custom",
            path: ["physicalPages"],
            message: "Physical page side must match production-ordinal parity.",
        });
    const textTurns = plan.readingTurns.filter(({ kind }) => kind === "TEXT_BEARING");
    const wordlessTurns = plan.readingTurns.filter(({ kind }) => kind === "WORDLESS_BREATH");
    const continuations = plan.readingTurns.filter((turn, index) => turn.kind === "TEXT_BEARING" &&
        index > 0 &&
        plan.readingTurns[index - 1].kind === "TEXT_BEARING" &&
        plan.readingTurns[index - 1].sourceBeatIds.join() === turn.sourceBeatIds.join());
    // A designed book may carry a beat's words on into the breath after it, so a continuation
    // takes a breath's place; every other book keeps eight text turns and four breaths. A picture
    // book's twelve beats are twelve text turns, with no breath and no design (2026-11-02).
    if (plan.narrativeBeats.length === 12
        ? textTurns.length !== 12 ||
            wordlessTurns.length !== 0 ||
            continuations.length > 0 ||
            Boolean(plan.bookDesign)
        : textTurns.length !== 8 + continuations.length ||
            wordlessTurns.length !== 4 - continuations.length ||
            (continuations.length > 0 && !plan.bookDesign))
        context.addIssue({
            code: "custom",
            path: ["readingTurns"],
            message: "The v1 default requires eight story turns and four wordless breaths; only a designed book may carry a beat on in a breath's place.",
        });
    for (const beat of plan.narrativeBeats) {
        const turns = plan.readingTurns.filter((turn) => turn.kind === "TEXT_BEARING" && turn.sourceBeatIds.includes(beat.beatId));
        if (turns.length < 2 && turns.every(({ textMomentIds }) => !textMomentIds))
            continue;
        const carried = turns.flatMap((turn) => bedtimeTurnTextMoments([beat], turn).map(({ textMomentId }) => textMomentId));
        const own = (beat.textMoments ?? []).map(({ textMomentId }) => textMomentId);
        if (turns.some(({ textMomentIds }) => !textMomentIds?.length) ||
            turns.some(({ textMomentIds }) => textMomentIds.some((id) => !own.includes(id))) ||
            carried.join() !== own.join())
            context.addIssue({
                code: "custom",
                path: ["readingTurns"],
                message: "A beat carried across turns must place each of its text moments exactly once, in reading order.",
            });
    }
    for (const [index, turn] of plan.readingTurns.entries()) {
        const expected = [4 + index * 2, 5 + index * 2];
        const pages = turn.physicalPageIds.map((pageId) => plan.physicalPages.find((page) => page.pageId === pageId)?.productionOrdinal);
        if (pages[0] !== expected[0] || pages[1] !== expected[1])
            context.addIssue({
                code: "custom",
                path: ["readingTurns", index, "physicalPageIds"],
                message: "Pages 4–27 must form twelve exact two-page reading turns.",
            });
        const grammar = plan.grammarCatalog.find(({ grammarId }) => grammarId === turn.grammarId);
        if (!grammar?.physicalEligible || !grammar.eligibleTurnKinds.includes(turn.kind))
            context.addIssue({
                code: "custom",
                path: ["readingTurns", index, "grammarId"],
                message: "Reading turn selected an ineligible physical grammar.",
            });
        if (turn.layoutVariantId &&
            !grammar?.layoutVariants?.some(({ variantId }) => variantId === turn.layoutVariantId))
            context.addIssue({
                code: "custom",
                path: ["readingTurns", index, "layoutVariantId"],
                message: "Reading turn selected a layout variant outside its governed grammar.",
            });
        const variant = grammar?.layoutVariants?.find(({ variantId }) => variantId === turn.layoutVariantId);
        const mediaSurface = plan.mediaSurfaces.find(({ turnId }) => turnId === turn.turnId);
        if (plan.compilationState === "COMPILED" &&
            (variant?.sourcePanelPacking || mediaSurface?.sourcePanelPacking)) {
            const slots = variant?.slots.filter(({ kind }) => kind === "MEDIA") ?? [];
            const panels = mediaSurface?.sourcePanels ?? [];
            const sameRect = (left, right) => left.xMilliPoints === right.xMilliPoints &&
                left.yMilliPoints === right.yMilliPoints &&
                left.widthMilliPoints === right.widthMilliPoints &&
                left.heightMilliPoints === right.heightMilliPoints;
            if (variant?.sourcePanelPacking !== mediaSurface?.sourcePanelPacking ||
                slots.length !== panels.length ||
                slots.some((slot, panelIndex) => panels[panelIndex]?.slotId !== slot.slotId ||
                    !sameRect(slot.region, panels[panelIndex].destinationRegion)))
                context.addIssue({
                    code: "custom",
                    path: ["readingTurns", index, "layoutVariantId"],
                    message: "Packed source panels must match the exact selected grammar slots and physical destinations.",
                });
            if (plan.compositionPlan) {
                const placements = plan.compositionPlan.surfaces.flatMap(({ placements }) => placements);
                for (const panel of panels) {
                    const placement = placements.find(({ slotId, segments }) => slotId === panel.slotId &&
                        segments.every(({ pageId }) => turn.physicalPageIds.includes(pageId)));
                    const crop = placement?.renderPrimitive.primitiveKind === "MEDIA"
                        ? placement.renderPrimitive.sourceCropPixels
                        : undefined;
                    if (!placement ||
                        !crop ||
                        !sameRect(placement.surfaceRect, panel.destinationRegion) ||
                        crop.x !== panel.sourceCropPixels.x ||
                        crop.y !== panel.sourceCropPixels.y ||
                        crop.width !== panel.sourceCropPixels.width ||
                        crop.height !== panel.sourceCropPixels.height)
                        context.addIssue({
                            code: "custom",
                            path: ["compositionPlan"],
                            message: "Every packed panel must execute its exact immutable source crop and destination.",
                        });
                }
            }
        }
    }
    if (Boolean(plan.bookDesign) !== Boolean(plan.authority.bookDesign))
        context.addIssue({
            code: "custom",
            path: ["bookDesign"],
            message: "A book design and its authority binding are present together or not at all.",
        });
    const beatIds = new Set(plan.narrativeBeats.map(({ beatId }) => beatId));
    if (plan.readingTurns.some(({ sourceBeatIds }) => sourceBeatIds.some((id) => !beatIds.has(id))))
        context.addIssue({
            code: "custom",
            path: ["readingTurns"],
            message: "Every reading turn must preserve an accepted narrative-beat binding.",
        });
    const typographyDecisions = plan.typographyDirectionDecisions ?? [];
    if (new Set(typographyDecisions.map(({ beatId }) => beatId)).size !== typographyDecisions.length)
        context.addIssue({
            code: "custom",
            path: ["typographyDirectionDecisions"],
            message: "Only one current typography-direction decision may govern each narrative beat.",
        });
    if (typographyDecisions.some(({ beatId }) => !beatIds.has(beatId)))
        context.addIssue({
            code: "custom",
            path: ["typographyDirectionDecisions"],
            message: "Typography direction must remain bound to a current accepted narrative beat.",
        });
    if (typographyDecisions.length > 0 && !plan.authority.typographyDirection)
        context.addIssue({
            code: "custom",
            path: ["authority", "typographyDirection"],
            message: "Edited typography direction requires an explicit immutable authority binding.",
        });
    for (const [decisionIndex, decision] of typographyDecisions.entries()) {
        const beat = plan.narrativeBeats.find(({ beatId }) => beatId === decision.beatId);
        if (beat &&
            (beat.textMoments?.map(({ momentDigestSha256 }) => momentDigestSha256).join(",") ?? "") !==
                decision.moments.map(({ momentDigestSha256 }) => momentDigestSha256).join(","))
            context.addIssue({
                code: "custom",
                path: ["typographyDirectionDecisions", decisionIndex, "moments"],
                message: "The accepted beat must expose the exact current typography decision.",
            });
    }
    if (new Set(plan.typesetTextBlocks.map(({ textBlockId }) => textBlockId)).size !==
        plan.typesetTextBlocks.length)
        context.addIssue({
            code: "custom",
            path: ["typesetTextBlocks"],
            message: "Every governed text block requires one stable unique identity.",
        });
    if (plan.compilationState === "COMPILED")
        for (const [turnIndex, turn] of plan.readingTurns.entries()) {
            if (turn.kind !== "TEXT_BEARING")
                continue;
            const moments = bedtimeTurnTextMoments(plan.narrativeBeats, turn);
            if (moments.length === 0)
                continue;
            const blocks = plan.typesetTextBlocks
                .filter(({ turnId }) => turnId === turn.turnId)
                .sort((left, right) => left.accessibilityOrderIndex - right.accessibilityOrderIndex);
            if (blocks.length !== moments.length ||
                moments.some((moment) => !blocks.some(({ typeDirection }) => typeDirection?.textMomentId === moment.textMomentId)))
                context.addIssue({
                    code: "custom",
                    path: ["readingTurns", turnIndex],
                    message: "Every approved text moment requires one exact executable text block.",
                });
            const slotIds = blocks.flatMap(({ typeDirection }) => typeDirection ? [typeDirection.grammarSlotId] : []);
            if (new Set(slotIds).size !== slotIds.length)
                context.addIssue({
                    code: "custom",
                    path: ["readingTurns", turnIndex],
                    message: "Text moments cannot collapse into the same governed grammar slot.",
                });
            const normalize = (value) => value.normalize("NFC").replace(/\s+/gu, " ").trim();
            const approved = moments.map(({ approvedText }) => approvedText).join(" ");
            const executed = blocks.flatMap(({ measuredLines }) => measuredLines).join(" ");
            if (normalize(executed) !== normalize(approved))
                context.addIssue({
                    code: "custom",
                    path: ["readingTurns", turnIndex],
                    message: "Executable text moments must preserve the complete approved wording.",
                });
        }
    if (plan.matterPages) {
        const matterOrdinals = plan.matterPages.map(({ productionOrdinal }) => productionOrdinal);
        if (matterOrdinals.join(",") !== "1,2,3,28,29,30,31,32")
            context.addIssue({
                code: "custom",
                path: ["matterPages"],
                message: "Governed Bedtime matter must bind exact pages 1–3 and 28–32.",
            });
        const blockIds = new Set(plan.typesetTextBlocks.map(({ textBlockId }) => textBlockId));
        if (plan.matterPages.some(({ textBlockIds }) => textBlockIds.some((id) => !blockIds.has(id))))
            context.addIssue({
                code: "custom",
                path: ["matterPages"],
                message: "Every governed matter-page block must resolve to exact typeset text.",
            });
    }
    if (plan.compositionRequest.bookId !== plan.bookId)
        context.addIssue({
            code: "custom",
            path: ["compositionRequest"],
            message: "Composition request must bind the same exact publication and book.",
        });
    if (plan.compilationState === "COMPILED" &&
        (!plan.compositionPlan ||
            plan.blockingDiagnostics.length > 0 ||
            plan.compositionPlan.bookId !== plan.bookId ||
            plan.compositionPlan.requestDigestSha256 !== plan.compositionRequest.requestDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["compilationState"],
            message: "A compiled publication requires one exact matching composition plan and no blockers.",
        });
    if (plan.compilationState === "COMPOSITION_BLOCKED" &&
        (plan.compositionPlan !== null || plan.blockingDiagnostics.length === 0))
        context.addIssue({
            code: "custom",
            path: ["blockingDiagnostics"],
            message: "A blocked publication must retain diagnostics and cannot carry a composition plan.",
        });
});
export const bedtimePublicationPlanV1Schema = bedtimePublicationPlanBodyV1Schema.safeExtend({
    planDigestSha256: digestSchema,
});
export const bedtimePublicationCommandActionSchema = z.enum([
    "ACCEPT_RECOMMENDED_PACING",
    "UPDATE_READING_TURN",
    "SELECT_PAGE_GRAMMAR",
    "SELECT_LAYOUT_VARIANT",
    "UPDATE_TYPOGRAPHY_DIRECTION",
    "RESET_TYPOGRAPHY_DIRECTION",
    "REBASE_TYPOGRAPHY_DIRECTION",
    "RECOMPILE_PUBLICATION_PLAN",
    "APPROVE_PHYSICAL_PACING",
    "APPROVE_PUBLICATION_PLAN",
    "INVALIDATE_PUBLICATION_PLAN",
]);
export const bedtimeTypographyMomentEditV1Schema = bedtimeTextMomentV1Schema
    .omit({
    contract: true,
    textMomentId: true,
    sequence: true,
    approvedText: true,
    lineationAuthority: true,
    momentDigestSha256: true,
})
    .extend({
    /** Present when the owner changed this moment's type size; absent, the design's size stays. */
    sizeChosen: z.literal(true).optional(),
})
    .strict();
export const bedtimeTypographyDirectionEditV1Schema = z
    .object({
    beatId: idSchema,
    expectedAcceptedMeaningDigestSha256: digestSchema,
    expectedMomentDigestsSha256: z.array(digestSchema).max(3),
    moments: z.array(bedtimeTypographyMomentEditV1Schema).min(1).max(3),
})
    .strict();
export const bedtimeTypographyRecoveryBindingV1Schema = z
    .object({
    expectedCurrentBookRevision: z.number().int().nonnegative(),
    expectedCurrentStoryRevision: z.number().int().nonnegative(),
    expectedCurrentSourceDigestSha256: digestSchema,
    reviewedDecisionDigestsSha256: z.array(digestSchema).max(12),
})
    .strict();
export const bedtimeTypographyRecoveryV1Schema = z
    .object({
    contract: z.literal("BEDTIME_TYPOGRAPHY_RECOVERY_V1"),
    state: z.enum(["CURRENT", "REVIEW_REQUIRED", "PREREQUISITES_BLOCKED"]),
    currentSource: bedtimeTypographyRecoveryBindingV1Schema.nullable(),
    rebaseEligible: z.boolean(),
    decisions: z
        .array(z
        .object({
        decisionId: idSchema,
        beatId: idSchema,
        previousApprovedText: z.string().max(2_000),
        currentApprovedText: z.string().max(2_000).nullable(),
        wordingUnchanged: z.boolean(),
    })
        .strict())
        .max(12),
    diagnostics: z
        .array(z
        .object({
        code: safeCodeSchema,
        path: z.string().max(500),
        message: z.string().min(1).max(2_000),
    })
        .strict())
        .max(100),
    truthBoundary: z.literal("RECOVERY_REQUIRES_EXACT_PREVIEW_AND_REVIEWED_APPLY"),
})
    .strict();
export const bedtimePublicationCommandV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_COMMAND_V1"),
    action: bedtimePublicationCommandActionSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    expectedStoryRevision: z.number().int().nonnegative(),
    expectedSourceRevision: z.number().int().nonnegative(),
    expectedPlanRevision: z.number().int().positive(),
    expectedPlanDigestSha256: digestSchema,
    expectedProfileRevision: z.number().int().nonnegative(),
    expectedConsentRevision: z.number().int().nonnegative(),
    expectedApprovalRevision: z.number().int().nonnegative(),
    expectedTypographyDirectionRevision: z.number().int().nonnegative().optional(),
    readingTurnId: idSchema.optional(),
    grammarId: bedtimePageGrammarIdSchema.optional(),
    layoutVariantId: idSchema.optional(),
    pageTurnPurpose: z.string().trim().min(1).max(500).optional(),
    invalidationCode: safeCodeSchema.optional(),
    typographyEdit: bedtimeTypographyDirectionEditV1Schema.optional(),
    typographyRecovery: bedtimeTypographyRecoveryBindingV1Schema.optional(),
    expectedImpactDigestSha256: digestSchema.optional(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.action === "UPDATE_READING_TURN" &&
        (!command.readingTurnId || !command.pageTurnPurpose))
        context.addIssue({
            code: "custom",
            path: ["readingTurnId"],
            message: "Updating a reading turn requires its identity and semantic page-turn purpose.",
        });
    if (command.action === "SELECT_PAGE_GRAMMAR" && (!command.readingTurnId || !command.grammarId))
        context.addIssue({
            code: "custom",
            path: ["grammarId"],
            message: "Selecting a grammar requires one exact reading turn and grammar.",
        });
    if (command.action === "SELECT_LAYOUT_VARIANT" &&
        (!command.readingTurnId || !command.layoutVariantId))
        context.addIssue({
            code: "custom",
            path: ["layoutVariantId"],
            message: "Selecting a layout variant requires one exact reading turn and variant.",
        });
    if (command.action === "UPDATE_TYPOGRAPHY_DIRECTION" && !command.typographyEdit)
        context.addIssue({
            code: "custom",
            path: ["typographyEdit"],
            message: "Updating typography direction requires one source-preserving beat edit.",
        });
    if (command.action === "UPDATE_TYPOGRAPHY_DIRECTION" &&
        command.expectedTypographyDirectionRevision === undefined)
        context.addIssue({
            code: "custom",
            path: ["expectedTypographyDirectionRevision"],
            message: "Typography edits must bind the exact current direction revision.",
        });
    if (["RESET_TYPOGRAPHY_DIRECTION", "REBASE_TYPOGRAPHY_DIRECTION"].includes(command.action) &&
        (!command.typographyRecovery || command.expectedTypographyDirectionRevision === undefined))
        context.addIssue({
            code: "custom",
            path: ["typographyRecovery"],
            message: "Recovery must bind both the saved direction and the reviewed current approved source.",
        });
    if (command.typographyRecovery &&
        !["RESET_TYPOGRAPHY_DIRECTION", "REBASE_TYPOGRAPHY_DIRECTION"].includes(command.action))
        context.addIssue({
            code: "custom",
            path: ["typographyRecovery"],
            message: "Recovery bindings cannot be attached to another publication action.",
        });
    if (command.typographyRecovery && command.typographyEdit)
        context.addIssue({
            code: "custom",
            path: ["typographyEdit"],
            message: "Recovery cannot also carry a new typography edit.",
        });
    if (command.action === "INVALIDATE_PUBLICATION_PLAN" && !command.invalidationCode)
        context.addIssue({
            code: "custom",
            path: ["invalidationCode"],
            message: "Invalidation requires a stable reason code.",
        });
});
export const bedtimePublicationCommandImpactV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_COMMAND_IMPACT_V1"),
    action: bedtimePublicationCommandActionSchema,
    expectedPlanRevision: z.number().int().positive(),
    expectedPlanDigestSha256: digestSchema,
    affectedReadingTurnIds: z.array(idSchema).max(12),
    affectedMediaSurfaceIds: z.array(idSchema).max(12).optional(),
    affectedAssetScopes: z.array(idSchema).max(24),
    requiredReevaluations: z.array(safeCodeSchema).max(24),
    providerCallRequired: z.boolean(),
    providerDispatchAuthorized: z.literal(false),
    validationState: z.enum(["VALID", "BLOCKED"]),
    blockingDiagnostics: z
        .array(z
        .object({
        code: safeCodeSchema,
        path: z.string().max(500),
        message: z.string().trim().min(1).max(2_000),
    })
        .strict())
        .max(100),
    typographyProposal: z
        .object({
        beatId: idSchema,
        editDigestSha256: digestSchema,
        requiredMomentCount: z.number().int().min(1).max(3),
        authoredLineCount: z.number().int().min(1).max(24),
        protectedPhraseCount: z.number().int().min(0).max(36),
        semanticRoles: z.array(bedtimeTextMomentRoleSchema).min(1).max(3),
        capacityByReadingTurn: z
            .array(z
            .object({
            readingTurnId: idSchema,
            grammarId: bedtimePageGrammarIdSchema,
            layoutVariantId: idSchema.optional(),
            availableTextSlots: z.number().int().min(0).max(12),
            requiredTextMoments: z.number().int().min(1).max(3),
            state: z.enum(["FITS", "BLOCKED"]),
        })
            .strict())
            .min(1)
            .max(2),
        eligibleGrammarAlternatives: z
            .array(z
            .object({
            grammarId: bedtimePageGrammarIdSchema,
            layoutVariantId: idSchema.optional(),
            availableTextSlots: z.number().int().min(1).max(12),
        })
            .strict())
            .max(24),
    })
        .strict()
        .optional(),
    impactDigestSha256: digestSchema,
    commandDigestSha256: digestSchema.optional(),
    compositionExecutionDigestSha256: digestSchema.nullable().optional(),
})
    .strict()
    .superRefine((impact, context) => {
    if ((impact.validationState === "BLOCKED") !== impact.blockingDiagnostics.length > 0)
        context.addIssue({
            code: "custom",
            path: ["blockingDiagnostics"],
            message: "Blocked impact previews require diagnostics and valid previews require none.",
        });
});
export const bedtimePublicationCommandPreviewV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_COMMAND_PREVIEW_V1"),
    impact: bedtimePublicationCommandImpactV1Schema,
    proposedPlan: bedtimePublicationPlanV1Schema.nullable(),
    truthBoundary: z.literal("EPHEMERAL_COMPOSITION_PREVIEW_NOT_SAVED_OR_APPROVED"),
})
    .strict()
    .superRefine((preview, context) => {
    if (preview.impact.validationState === "VALID" &&
        (!preview.proposedPlan?.compositionPlan ||
            !preview.impact.compositionExecutionDigestSha256 ||
            preview.proposedPlan.planRevision !== preview.impact.expectedPlanRevision + 1 ||
            preview.proposedPlan.approvalState !== "DRAFT"))
        context.addIssue({
            code: "custom",
            path: ["proposedPlan"],
            message: "A valid ephemeral preview requires the exact next draft composition and execution digest.",
        });
});
export const bedtimePublicationCompileCommandV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_COMPILE_COMMAND_V1"),
    expectedBookRevision: z.number().int().nonnegative(),
    expectedStoryRevision: z.number().int().nonnegative(),
    expectedSourceRevision: z.number().int().nonnegative(),
})
    .strict();
export const bedtimePublicationPrerequisiteV1Schema = z
    .object({
    code: safeCodeSchema,
    state: z.enum(["READY", "BLOCKED"]),
    label: z.string().trim().min(1).max(240),
    resolution: z.string().trim().min(1).max(500),
})
    .strict();
export const bedtimeVisualComplianceCriterionV1Schema = z.enum([
    "ACCEPTED_EVENT_ONLY",
    "EXACT_ENTITY_COUNTS",
    "NO_UNAUTHORIZED_PROPS_OR_TEXT",
    "CHARACTER_WORLD_CONTINUITY",
    "NO_PROHIBITED_FUTURE_STATE",
    "TOPOLOGY_AND_GUTTER_SAFETY",
]);
/**
 * Owner decision 5 (2026-10-13): after a page's second paint, the buyer may keep a final picture
 * whose check doubted only these. A fold or words-area failure is never kept.
 *
 * Owner decision 3 (2026-10-15) adds the count: live, the checker failed one page on every repaint
 * because the painter drew the people its words named while the page's own brief counted none
 * (run 38654606576, 2026-10-14). The buyer, who can count, decides.
 */
export const BEDTIME_VISUAL_CHECK_KEEPABLE_CRITERIA = new Set([
    "ACCEPTED_EVENT_ONLY",
    "EXACT_ENTITY_COUNTS",
    "NO_UNAUTHORIZED_PROPS_OR_TEXT",
    "CHARACTER_WORLD_CONTINUITY",
    "NO_PROHIBITED_FUTURE_STATE",
]);
export const BEDTIME_VISUAL_CHECK_KEEP_POLICY = "BUYER_KEEPS_AFTER_SECOND_PAINT_2026-10-15.1";
/**
 * A sample picture the check failed on its second paint or later (B7, never strand, 2026-11-03).
 * Samples only show the look before the pages are painted and are never printed: each page is
 * painted again for the book and checked again, under decision 5. So a sample may be kept over any
 * criterion once its page has been painted twice; live, a sample failed its words' area four times
 * running and the book could go no further.
 */
export const BEDTIME_VISUAL_SAMPLE_KEEP_POLICY = "BUYER_KEEPS_SAMPLE_AFTER_SECOND_PAINT_2026-11-03.1";
/** Pictures kept before the count became keepable keep the policy they were kept under. */
const BEDTIME_VISUAL_CHECK_KEEP_POLICIES = [
    "BUYER_KEEPS_AFTER_SECOND_PAINT_2026-10-13.1",
    BEDTIME_VISUAL_CHECK_KEEP_POLICY,
    BEDTIME_VISUAL_SAMPLE_KEEP_POLICY,
];
const keepableCriteriaByPolicy = {
    "BUYER_KEEPS_AFTER_SECOND_PAINT_2026-10-13.1": new Set([
        "ACCEPTED_EVENT_ONLY",
        "NO_UNAUTHORIZED_PROPS_OR_TEXT",
        "CHARACTER_WORLD_CONTINUITY",
        "NO_PROHIBITED_FUTURE_STATE",
    ]),
    [BEDTIME_VISUAL_CHECK_KEEP_POLICY]: BEDTIME_VISUAL_CHECK_KEEPABLE_CRITERIA,
    [BEDTIME_VISUAL_SAMPLE_KEEP_POLICY]: new Set([
        ...BEDTIME_VISUAL_CHECK_KEEPABLE_CRITERIA,
        "TOPOLOGY_AND_GUTTER_SAFETY",
    ]),
};
/**
 * The keep policy a picture is judged by now: a final picture's, or a sample's. A review that does
 * not say which request painted it is held to the final picture's stricter rule.
 */
export function bedtimeVisualCheckKeepPolicyFor(review) {
    return review.requestId === undefined || bedtimeVisualReviewIsFinalPicture(review)
        ? BEDTIME_VISUAL_CHECK_KEEP_POLICY
        : BEDTIME_VISUAL_SAMPLE_KEEP_POLICY;
}
const bedtimeVisualCheckOverrideV1Schema = z
    .object({
    policy: z.enum(BEDTIME_VISUAL_CHECK_KEEP_POLICIES),
    priorFailedPaints: z.number().int().min(1).max(50),
    keptCriteria: z.array(bedtimeVisualComplianceCriterionV1Schema).min(1).max(6),
})
    .strict();
/**
 * The criteria a buyer would be keeping this picture over, or null when it cannot be kept: its
 * check passed, or something it failed is never keepable.
 */
export function bedtimeVisualCheckKeepableCriteria(review, 
/**
 * The policy a stored keep was made under; a new keep is judged by the current one for its kind
 * of picture (a final picture's, or a sample's).
 */
policy = bedtimeVisualCheckKeepPolicyFor(review)) {
    if (review.automatedReview.decision === "PASS")
        return null;
    const keepable = keepableCriteriaByPolicy[policy];
    const doubted = (review.automatedReview.findings ?? [])
        .filter(({ state, confidenceMillionths }) => state !== "PASS" || confidenceMillionths < 800_000)
        .map(({ criterion }) => criterion);
    if (!doubted.length || doubted.some((criterion) => !keepable.has(criterion)))
        return null;
    return [...new Set(doubted)].sort();
}
const bedtimeVisualEvidenceRegionV1Schema = z
    .object({
    xMillionths: z.number().int().min(0).max(1_000_000),
    yMillionths: z.number().int().min(0).max(1_000_000),
    widthMillionths: z.number().int().positive().max(1_000_000),
    heightMillionths: z.number().int().positive().max(1_000_000),
})
    .strict()
    .superRefine((region, context) => {
    if (region.xMillionths + region.widthMillionths > 1_000_000 ||
        region.yMillionths + region.heightMillionths > 1_000_000)
        context.addIssue({
            code: "custom",
            message: "Visual-evidence regions must remain inside the reviewed image.",
        });
});
export const bedtimeVisualComplianceFindingV1Schema = z
    .object({
    criterion: bedtimeVisualComplianceCriterionV1Schema,
    state: z.enum(["PASS", "FAIL", "INDETERMINATE"]),
    confidenceMillionths: z.number().int().min(0).max(1_000_000),
    evidence: z.string().trim().min(1).max(1_000),
    regions: z.array(bedtimeVisualEvidenceRegionV1Schema).max(12),
})
    .strict();
export const bedtimePublicationIdentityReferenceV1Schema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_IDENTITY_REFERENCE_V1"),
    role: z.enum(["CHARACTER_WORLD_BIBLE", "LIKENESS"]),
    sourceArtifactId: idSchema,
    assetId: idSchema,
    assetObjectVersionId: z.string().min(1).max(1_024),
    assetChecksumSha256: digestSchema,
    authorizationDigestSha256: digestSchema,
    identityAnchors: z.array(z.string().trim().min(1).max(1_000)).min(1).max(20),
    referenceForm: z.enum([
        "NEUTRAL_CHARACTER_IDENTITY_BOARD",
        "SCENE_REFERENCE_LOCAL_DIAGNOSTIC_ONLY",
    ]),
    scope: z.literal("IDENTITY_STYLE_WORLD_ONLY_NO_COMPOSITION_OR_EVENT_TRANSFER"),
})
    .strict();
/**
 * The cover direction the family chose with the storyboard. The final front art follows its front
 * cover's framing, focal arrangement, title space and palette, and never its event, its characters'
 * likeness, or anything of its back cover, spine or marks.
 */
export const bedtimeCoverDirectionReferenceV1Schema = z
    .object({
    contract: z.literal("BEDTIME_COVER_DIRECTION_REFERENCE_V1"),
    /** The storyboard approval that chose this direction. */
    approvalId: idSchema,
    /** The cover-concepts artifact that holds it. */
    sourceArtifactId: idSchema,
    assetId: idSchema,
    assetObjectVersionId: z.string().min(1).max(1_024),
    assetChecksumSha256: digestSchema,
    authorizationDigestSha256: digestSchema,
    scope: z.literal("FRONT_COMPOSITION_FRAMING_AND_PALETTE_ONLY_NO_EVENT_CAST_OR_MARK_TRANSFER"),
})
    .strict();
/**
 * How many one-picture sample repaints ("Paint one replacement picture") can follow one another
 * and still count for the three-picture sample set they started from. A repaint past this never
 * counts, so it is never offered (review, 2026-11-02).
 */
export const BEDTIME_SAMPLE_REPLACEMENT_CHAIN_LIMIT = 6;
/**
 * A review of a final picture, made by the artwork step at print size (its request is
 * `native-<surface>`), not of a sample. Decided by where the picture came from, never by its
 * resolution: a small sample can be sharp enough to print too.
 */
export function bedtimeVisualReviewIsFinalPicture(review) {
    return typeof review.requestId === "string" && review.requestId.startsWith("native-");
}
export const bedtimeVisualComplianceReviewV1Schema = z
    .object({
    contract: z.literal("BEDTIME_VISUAL_COMPLIANCE_REVIEW_V1"),
    reviewId: idSchema,
    sourceProofSetId: idSchema.optional(),
    planDigestSha256: digestSchema,
    requestId: idSchema,
    mediaSurfaceId: idSchema,
    turnId: idSchema,
    topology: bedtimeMediaTopologySchema,
    assetId: idSchema,
    assetObjectVersionId: idSchema,
    assetChecksumSha256: digestSchema,
    identityReferences: z.array(bedtimePublicationIdentityReferenceV1Schema).max(4).optional(),
    resolutionAssessment: z.lazy(() => bedtimeMediaResolutionAssessmentV1Schema).optional(),
    automatedReview: z
        .object({
        method: z.literal("MULTIMODAL_PIXEL_REVIEW"),
        modelId: idSchema,
        providerRequestId: idSchema,
        requestDigestSha256: digestSchema,
        reviewImage: z
            .object({
            kind: z.literal("EXACT_COMPOSED_PLAN_PREVIEW"),
            checksumSha256: digestSchema,
            assetId: idSchema.optional(),
            assetObjectVersionId: z.string().min(1).max(1_024).optional(),
            widthPixels: z.number().int().positive().max(10_000),
            heightPixels: z.number().int().positive().max(10_000),
            mediaPlacementCount: z.number().int().positive().max(12),
            liveTextNodeIds: z.array(idSchema).max(20),
        })
            .strict()
            .optional(),
        reviewPdf: z
            .object({
            kind: z.literal("DETERMINISTIC_COMPOSED_REVIEW_PDF"),
            checksumSha256: digestSchema,
            assetId: idSchema,
            assetObjectVersionId: z.string().min(1).max(1_024),
            pageCount: z.union([z.literal(1), z.literal(2)]),
            liveTextNodeIds: z.array(idSchema).max(20),
        })
            .strict()
            .optional(),
        reviewedAt: timestampSchema,
        findings: z.array(bedtimeVisualComplianceFindingV1Schema).length(6),
        decision: z.enum(["PASS", "FAIL", "NEEDS_HUMAN"]),
    })
        .strict(),
    humanDecision: z
        .object({
        state: z.enum(["PENDING", "APPROVED", "REJECTED"]),
        actorId: idSchema.nullable(),
        decidedAt: timestampSchema.nullable(),
        note: z.string().trim().min(1).max(1_000).nullable(),
        checkOverride: bedtimeVisualCheckOverrideV1Schema.optional(),
    })
        .strict(),
    replacementOf: z
        .object({
        reviewId: idSchema,
        reviewDigestSha256: digestSchema,
        assetId: idSchema,
        assetObjectVersionId: idSchema,
        assetChecksumSha256: digestSchema,
        reasonCodes: z.array(safeCodeSchema).min(1).max(12),
    })
        .strict()
        .optional(),
    finalDecision: z.enum(["PENDING_HUMAN", "APPROVED", "REJECTED"]),
    truthBoundary: z.literal("AUTOMATED_REVIEW_REQUIRES_EXACT_ASSET_HUMAN_APPROVAL"),
    reviewDigestSha256: digestSchema,
})
    .strict()
    .superRefine((review, context) => {
    const criteria = review.automatedReview.findings.map(({ criterion }) => criterion);
    if (new Set(criteria).size !== bedtimeVisualComplianceCriterionV1Schema.options.length)
        context.addIssue({
            code: "custom",
            path: ["automatedReview", "findings"],
            message: "Visual compliance requires every criterion exactly once.",
        });
    const findingsDecision = review.automatedReview.findings.some(({ state }) => state === "FAIL")
        ? "FAIL"
        : review.automatedReview.findings.some(({ state, confidenceMillionths }) => state === "INDETERMINATE" || confidenceMillionths < 800_000)
            ? "NEEDS_HUMAN"
            : "PASS";
    if (review.automatedReview.decision !== findingsDecision)
        context.addIssue({
            code: "custom",
            path: ["automatedReview", "decision"],
            message: "The automated decision must be derived from the exact criterion findings.",
        });
    const hasHumanIdentity = Boolean(review.humanDecision.actorId && review.humanDecision.decidedAt && review.humanDecision.note);
    if ((review.humanDecision.state !== "PENDING") !== hasHumanIdentity)
        context.addIssue({
            code: "custom",
            path: ["humanDecision"],
            message: "A completed human decision requires actor, time, and note evidence.",
        });
    const override = review.humanDecision.checkOverride;
    const keepable = bedtimeVisualCheckKeepableCriteria(review, override?.policy);
    // A kept picture names exactly what its check doubted, and only keepable criteria. A sample is
    // kept only under the sample policy, and a final picture only under a final picture's.
    const validOverride = Boolean(override &&
        review.humanDecision.state === "APPROVED" &&
        bedtimeVisualReviewIsFinalPicture(review) ===
            (override.policy !== BEDTIME_VISUAL_SAMPLE_KEEP_POLICY) &&
        keepable &&
        JSON.stringify([...override.keptCriteria].sort()) === JSON.stringify(keepable));
    if (override && !validOverride)
        context.addIssue({
            code: "custom",
            path: ["humanDecision", "checkOverride"],
            message: "A kept picture may override only the keepable criteria its check doubted.",
        });
    if (review.humanDecision.state === "APPROVED" &&
        review.automatedReview.decision !== "PASS" &&
        !validOverride)
        context.addIssue({
            code: "custom",
            path: ["humanDecision", "state"],
            message: "Human approval cannot override a failed or indeterminate automated review.",
        });
    const expectedFinal = review.humanDecision.state === "REJECTED"
        ? "REJECTED"
        : review.humanDecision.state === "APPROVED" && validOverride
            ? "APPROVED"
            : review.automatedReview.decision === "FAIL"
                ? "REJECTED"
                : review.automatedReview.decision === "PASS" &&
                    review.humanDecision.state === "APPROVED"
                    ? "APPROVED"
                    : "PENDING_HUMAN";
    if (review.finalDecision !== expectedFinal)
        context.addIssue({
            code: "custom",
            path: ["finalDecision"],
            message: "Final visual approval must combine automated and human decisions fail closed.",
        });
});
/**
 * Whether native pixels print at 300 DPI across a span. 300 DPI is 240 millipoints a pixel. A
 * layout rounds each picture edge to the nearest pixel, so one pixel short of the exact size is
 * allowed (a live steps page, 2026-10-12: 2567px for 2567.2px), and no more.
 */
export function bedtimePixelsMeetPrintDpi(pixels, milliPoints) {
    return (pixels + 1) * 240 >= milliPoints;
}
export const bedtimeMediaResolutionAssessmentV1Schema = z
    .object({
    contract: z.literal("BEDTIME_MEDIA_RESOLUTION_ASSESSMENT_V1"),
    planDigestSha256: digestSchema,
    assetChecksumSha256: digestSchema,
    sourceCropPixels: z
        .object({
        x: z.number().int().nonnegative(),
        y: z.number().int().nonnegative(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    })
        .strict(),
    governedCanvasPixels: z
        .object({ width: z.number().int().positive(), height: z.number().int().positive() })
        .strict(),
    destinationSizeMilliPoints: z
        .object({ width: z.number().int().positive(), height: z.number().int().positive() })
        .strict(),
    effectiveDpi: z
        .object({
        horizontal: z.number().positive().max(10_000),
        vertical: z.number().positive().max(10_000),
        minimum: z.number().positive().max(10_000),
    })
        .strict(),
    requiredMinimumDpi: z.literal(300),
    aspectErrorMillionths: z.number().int().nonnegative().max(1_000_000),
    interpolation: z.enum(["NONE", "LOCAL_UPSCALE_NOT_NATIVE"]),
    decision: z.enum(["PRINT_ELIGIBLE", "REVIEW_ONLY", "ASPECT_INVALID"]),
    truthBoundary: z.literal("NATIVE_SOURCE_PIXELS_ONLY_LOCAL_UPSCALE_EARNS_NO_PRINT_DPI"),
    assessmentDigestSha256: digestSchema,
})
    .strict()
    .superRefine((assessment, context) => {
    const expectedMinimum = Math.min(assessment.effectiveDpi.horizontal, assessment.effectiveDpi.vertical);
    if (Math.abs(assessment.effectiveDpi.minimum - expectedMinimum) > 0.01)
        context.addIssue({
            code: "custom",
            path: ["effectiveDpi", "minimum"],
            message: "Minimum DPI must be derived from both governed axes.",
        });
    const expectedDecision = assessment.aspectErrorMillionths > 15_000
        ? "ASPECT_INVALID"
        : bedtimePixelsMeetPrintDpi(assessment.sourceCropPixels.width, assessment.destinationSizeMilliPoints.width) &&
            bedtimePixelsMeetPrintDpi(assessment.sourceCropPixels.height, assessment.destinationSizeMilliPoints.height) &&
            assessment.interpolation === "NONE"
            ? "PRINT_ELIGIBLE"
            : "REVIEW_ONLY";
    if (assessment.decision !== expectedDecision)
        context.addIssue({
            code: "custom",
            path: ["decision"],
            message: "Resolution eligibility must use native pixels, aspect, and the 300-DPI floor.",
        });
});
export const bedtimeVisualComplianceDecisionCommandV1Schema = z
    .object({
    contract: z.literal("BEDTIME_VISUAL_COMPLIANCE_DECISION_COMMAND_V1"),
    /** KEEP: decision 5, keeping a final picture whose check doubted only keepable criteria. */
    action: z.enum(["APPROVE", "REJECT", "KEEP"]),
    expectedReviewDigestSha256: digestSchema,
    expectedPlanDigestSha256: digestSchema,
    expectedAssetChecksumSha256: digestSchema,
    note: z.string().trim().min(1).max(1_000),
})
    .strict();
const nativePixelRectSchema = z
    .object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
})
    .strict();
/**
 * A governed enlargement for a surface no provider canvas can paint at 300 DPI: the centered
 * painted window of the provider image is resampled once to the exact print pixels. Its effective
 * resolution is the painted window's, never the nominal 300 DPI, and it may not fall below 150.
 */
export const bedtimeNativeEnlargementSchema = z
    .object({
    method: z.literal("CENTERED_WINDOW_LANCZOS3_V1"),
    paintedWindow: nativePixelRectSchema,
    effectiveDpi: z.number().int().min(150).max(299),
})
    .strict();
/** True when an enlargement does not exactly describe a needed, centered, aspect-true window. */
export function bedtimeNativeEnlargementInvalid(canvas, target, enlargement) {
    const window = enlargement.paintedWindow;
    return (window.width > canvas.width ||
        window.height > canvas.height ||
        window.x !== Math.floor((canvas.width - window.width) / 2) ||
        window.y !== Math.floor((canvas.height - window.height) / 2) ||
        (window.width >= target.width && window.height >= target.height) ||
        Math.abs(window.width / window.height / (target.width / target.height) - 1) > 0.01 ||
        enlargement.effectiveDpi !==
            Math.floor(300 * Math.min(window.width / target.width, window.height / target.height)));
}
const nativeProviderPanelSchema = z
    .object({
    requestId: idSchema,
    sourceCropPixels: nativePixelRectSchema,
    providerCanvas: z
        .object({
        width: z.number().int().positive().max(3840),
        height: z.number().int().positive().max(3840),
        size: z.string().regex(/^\d{3,4}x\d{3,4}$/u),
    })
        .strict(),
    prompt: z.string().trim().min(1).max(16_000),
    promptDigestSha256: digestSchema,
})
    .strict();
export const bedtimeNativeMediaRequestV1Schema = z
    .object({
    contract: z.literal("BEDTIME_NATIVE_MEDIA_REQUEST_V1"),
    requestId: idSchema,
    mediaSurfaceId: idSchema,
    turnId: idSchema,
    useId: idSchema,
    useDigestSha256: digestSchema,
    planDigestSha256: digestSchema,
    topology: bedtimeMediaTopologySchema,
    targetDpi: z.literal(300),
    widthPixels: z.number().int().positive(),
    heightPixels: z.number().int().positive(),
    providerCanvas: z
        .object({
        width: z.number().int().positive().max(3840),
        height: z.number().int().positive().max(3840),
        size: z.string().regex(/^\d{3,4}x\d{3,4}$/u),
    })
        .strict()
        .nullable(),
    enlargement: bedtimeNativeEnlargementSchema.optional(),
    providerPanels: z.array(nativeProviderPanelSchema).min(2).max(3).optional(),
    placementTargets: z
        .array(z
        .object({
        nodeId: idSchema,
        pageIds: z.array(idSchema).min(1).max(2),
        destinationRegion: bookPageRectV1Schema,
        sourceCropPixels: nativePixelRectSchema,
    })
        .strict())
        .min(1)
        .max(6),
    prompt: z.string().trim().min(1).max(16_000),
    promptDigestSha256: digestSchema,
    identityReferences: z.array(bedtimePublicationIdentityReferenceV1Schema).max(4),
    quality: z.literal("medium"),
    generatedTypography: z.literal(false),
    // One reserved repaint of the words' area, only for a designed book's single scene carrying
    // words on its painting (2026-10-10); every other picture stays false.
    automaticRepairAllowed: z.boolean(),
    eligibility: z.enum(["SUPPORTED_NATIVE_REQUEST", "BLOCKED"]),
    blockingDiagnostics: z
        .array(z
        .object({
        code: safeCodeSchema,
        message: z.string().min(1).max(2_000),
    })
        .strict())
        .max(20),
})
    .strict()
    .superRefine((request, context) => {
    const provider = request.providerCanvas;
    if (provider &&
        (provider.width % 16 !== 0 ||
            provider.height % 16 !== 0 ||
            provider.size !== `${provider.width}x${provider.height}` ||
            provider.width * provider.height < 655_360 ||
            provider.width * provider.height > 8_294_400 ||
            Math.max(provider.width, provider.height) / Math.min(provider.width, provider.height) > 3 ||
            (request.enlargement
                ? bedtimeNativeEnlargementInvalid(provider, { width: request.widthPixels, height: request.heightPixels }, request.enlargement)
                : provider.width < request.widthPixels || provider.height < request.heightPixels)))
        context.addIssue({
            code: "custom",
            path: ["providerCanvas"],
            message: "The provider canvas must contain the complete native canvas, or its exact governed enlargement window, and satisfy exact request limits.",
        });
    if (request.enlargement && (!provider || request.providerPanels))
        context.addIssue({
            code: "custom",
            path: ["enlargement"],
            message: "A governed enlargement belongs to one single-scene provider canvas.",
        });
    if (request.eligibility === "SUPPORTED_NATIVE_REQUEST" &&
        (!provider || request.blockingDiagnostics.length || !request.identityReferences.length))
        context.addIssue({
            code: "custom",
            path: ["eligibility"],
            message: "Native dispatch requires supported geometry and current neutral continuity evidence.",
        });
    if (request.providerPanels) {
        const panels = request.providerPanels;
        if (request.topology !== "SEQUENTIAL_REGIONS" ||
            request.placementTargets.length !== panels.length ||
            new Set(panels.map((p) => p.requestId)).size !== panels.length)
            context.addIssue({
                code: "custom",
                path: ["providerPanels"],
                message: "Independent panels require one distinct sequential placement each.",
            });
        for (const [index, panel] of panels.entries()) {
            const c = panel.providerCanvas;
            const r = panel.sourceCropPixels;
            const target = request.placementTargets[index]?.sourceCropPixels;
            if (!target ||
                ["x", "y", "width", "height"].some((k) => r[k] !== target[k]) ||
                c.width < r.width ||
                c.height < r.height ||
                c.width % 16 ||
                c.height % 16 ||
                c.size !== `${c.width}x${c.height}` ||
                c.width * c.height < 655360 ||
                c.width * c.height > 8294400 ||
                Math.max(c.width, c.height) / Math.min(c.width, c.height) > 3)
                context.addIssue({
                    code: "custom",
                    path: ["providerPanels", index],
                    message: "Each panel must preserve its exact native crop and supported provider geometry.",
                });
        }
        const crops = panels.map((p) => p.sourceCropPixels);
        if (crops.some((a, index) => crops
            .slice(index + 1)
            .some((b) => a.x < b.x + b.width &&
            b.x < a.x + a.width &&
            a.y < b.y + b.height &&
            b.y < a.y + a.height)))
            context.addIssue({
                code: "custom",
                path: ["providerPanels"],
                message: "Native panels cannot overlap.",
            });
    }
    for (const [index, target] of request.placementTargets.entries()) {
        const crop = target.sourceCropPixels;
        const region = target.destinationRegion;
        if (request.eligibility === "SUPPORTED_NATIVE_REQUEST" &&
            (crop.x + crop.width > request.widthPixels ||
                crop.y + crop.height > request.heightPixels ||
                !bedtimePixelsMeetPrintDpi(crop.width, region.widthMilliPoints) ||
                !bedtimePixelsMeetPrintDpi(crop.height, region.heightMilliPoints) ||
                Math.abs(crop.width / crop.height / (region.widthMilliPoints / region.heightMilliPoints) - 1) > 0.015))
            context.addIssue({
                code: "custom",
                path: ["placementTargets", index],
                message: "Every accepted crop must provide native 300 DPI on both physical axes.",
            });
    }
});
// Only model families already supported by the shared image adapter and pricing table.
export const bedtimeNativeImageModelIdSchema = z
    .string()
    .regex(/^gpt-image-(?:2|2\.5-(?:flare|sunburst))(?:-20\d{2}-\d{2}-\d{2})?$/u);
export const bedtimeNativeMediaManifestV1Schema = z
    .object({
    contract: z.literal("BEDTIME_NATIVE_MEDIA_MANIFEST_V1"),
    bookId: idSchema,
    planRevision: z.number().int().positive(),
    planDigestSha256: digestSchema,
    printProfileDigestSha256: digestSchema,
    sourceRevision: z.number().int().nonnegative(),
    quality: z.literal("medium"),
    modelId: bedtimeNativeImageModelIdSchema,
    requests: z.array(bedtimeNativeMediaRequestV1Schema).max(12),
    automaticRepairAllowed: z.boolean(),
    maximumImagesPerAction: z.literal(3),
    coverState: z.literal("SEPARATE_COVER_SOURCE_REQUIRED"),
    manifestDigestSha256: digestSchema,
    truthBoundary: z.literal("NATIVE_REQUEST_FEASIBILITY_NOT_GENERATED_APPROVED_OR_PRINT_READY"),
})
    .strict()
    .superRefine((manifest, context) => {
    if (new Set(manifest.requests.map(({ useId }) => useId)).size !== manifest.requests.length)
        context.addIssue({
            code: "custom",
            path: ["requests"],
            message: "Native requests must use distinct exact composition use IDs.",
        });
    for (const [index, request] of manifest.requests.entries())
        if (request.planDigestSha256 !== manifest.planDigestSha256)
            context.addIssue({
                code: "custom",
                path: ["requests", index],
                message: "Every request must bind this exact publication plan.",
            });
});
export const bedtimeNativeCoverRequestV1Schema = z
    .object({
    contract: z.literal("BEDTIME_NATIVE_COVER_REQUEST_V1"),
    requestId: idSchema,
    bookId: idSchema,
    planRevision: z.number().int().positive(),
    planDigestSha256: digestSchema,
    coverSourceDigestSha256: digestSchema,
    coverBriefDigestSha256: digestSchema,
    printProfileDigestSha256: digestSchema,
    sourceRevision: z.number().int().nonnegative(),
    useId: z.literal("bedtime:canonical-cover-front"),
    destinationRegion: bookPageRectV1Schema,
    minimumNativeSourcePixels: z
        .object({ width: z.number().int().positive(), height: z.number().int().positive() })
        .strict(),
    providerCanvas: z
        .object({
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        size: z.string().regex(/^\d+x\d+$/u),
    })
        .strict()
        .nullable(),
    cropPolicy: z.literal("CENTERED_COVER_FILL"),
    requiredGeneratedAssetKind: z.literal("SHOWCASE_COVER"),
    identityReferences: z.array(bedtimePublicationIdentityReferenceV1Schema).max(4),
    /** Absent when no cover direction was chosen, as on every request made before it existed. */
    coverDirectionReference: bedtimeCoverDirectionReferenceV1Schema.optional(),
    targetDpi: z.literal(300),
    modelId: bedtimeNativeImageModelIdSchema,
    quality: z.literal("medium"),
    prompt: z.string().min(1).max(16000),
    promptDigestSha256: digestSchema,
    generatedTypography: z.literal(false),
    automaticRepairAllowed: z.literal(false),
    maximumImagesPerAction: z.literal(1),
    eligibility: z.enum(["SUPPORTED_NATIVE_REQUEST", "BLOCKED"]),
    blockingDiagnostics: z
        .array(z.object({ code: safeCodeSchema, message: z.string().min(1).max(2000) }).strict())
        .max(20),
    truthBoundary: z.literal("NATIVE_FRONT_REQUEST_NOT_GENERATED_COMPOSED_OR_APPROVED_COVER"),
    requestDigestSha256: digestSchema,
})
    .strict()
    .superRefine((request, context) => {
    // The painter takes at most four reference pictures in all.
    if (request.identityReferences.length + (request.coverDirectionReference ? 1 : 0) > 4)
        context.addIssue({
            code: "custom",
            path: ["coverDirectionReference"],
            message: "A native cover request carries at most four reference pictures in all.",
        });
    const canvas = request.providerCanvas;
    const ratio = request.destinationRegion.widthMilliPoints / request.destinationRegion.heightMilliPoints;
    const cropWidth = canvas ? Math.floor(Math.min(canvas.width, canvas.height * ratio)) : 0;
    const cropHeight = canvas ? Math.floor(Math.min(canvas.height, canvas.width / ratio)) : 0;
    if (canvas &&
        (canvas.width % 16 ||
            canvas.height % 16 ||
            Math.max(canvas.width, canvas.height) > 3840 ||
            canvas.width * canvas.height < 655360 ||
            canvas.width * canvas.height > 8294400 ||
            Math.max(canvas.width / canvas.height, canvas.height / canvas.width) > 3 ||
            canvas.size !== `${canvas.width}x${canvas.height}`))
        context.addIssue({
            code: "custom",
            path: ["providerCanvas"],
            message: "Native cover requests must use an exact supported provider canvas.",
        });
    if (request.eligibility === "SUPPORTED_NATIVE_REQUEST" &&
        (!canvas ||
            request.blockingDiagnostics.length ||
            !request.identityReferences.length ||
            request.identityReferences.some(({ referenceForm }) => referenceForm !== "NEUTRAL_CHARACTER_IDENTITY_BOARD") ||
            cropWidth < request.minimumNativeSourcePixels.width ||
            cropHeight < request.minimumNativeSourcePixels.height ||
            cropWidth < Math.ceil((request.destinationRegion.widthMilliPoints / 72000) * 300) ||
            cropHeight < Math.ceil((request.destinationRegion.heightMilliPoints / 72000) * 300)))
        context.addIssue({
            code: "custom",
            path: ["eligibility"],
            message: "The centered native front-art crop must satisfy both physical axes, current neutral identity and all request gates.",
        });
});
/**
 * Whether a stored review approves its picture for the book: it re-parses under the strict
 * contract and is approved, either with a passed check or with a kept-picture override the
 * contract has already validated (decision 5). Print paths use this, never the check alone.
 */
export function bedtimeVisualReviewApprovedForUse(stored) {
    const row = stored && typeof stored === "object"
        ? Object.fromEntries(Object.entries(stored).filter(([key]) => !["PK", "SK", "entityType"].includes(key)))
        : stored;
    const parsed = bedtimeVisualComplianceReviewV1Schema.safeParse(row);
    return (parsed.success &&
        parsed.data.finalDecision === "APPROVED" &&
        (parsed.data.automatedReview.decision === "PASS" ||
            // A kept sample only approved the look; it never prints (B7, 2026-11-03).
            (Boolean(parsed.data.humanDecision.checkOverride) &&
                parsed.data.humanDecision.checkOverride.policy !== BEDTIME_VISUAL_SAMPLE_KEEP_POLICY)));
}
//# sourceMappingURL=bedtime-publication.js.map